/**
 * /forgot-password — two-step UI for the Cognito self-serve password
 * reset flow.
 *
 *   Step 1 (EmailForm)    — email entry → calls Cognito `forgotPassword`
 *                           which mails a 6-digit code.
 *   Step 2 (EmailSent)    — success view: "We sent a code to <masked>".
 *                           Offers a "Resend code" button + a
 *                           "Continue → Reset password" link that hands
 *                           the email to /reset-password as a query param.
 *
 * 1:1 port from the legacy `3mote-fe/src/pages/auth/ForgotPassword/`,
 * adapted for Cognito-with-code (vs the legacy email-with-link flow).
 *
 * UX note: Cognito's `preventUserExistenceErrors: true` (set on the
 * deployed User Pool) means an unknown email returns the same shape as
 * a known one. We display the success state regardless so we don't
 * leak account existence — matching the legacy behavior described in
 * the audit.
 */

import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { forgotPassword } from '@/auth/cognito'
import { Button } from '@/components/Button'
import { FormField } from '@/components/FormField'
import { describeForgotPasswordError } from '@/features/auth/describe-error'

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  const { t } = useTranslation('auth')
  const [step, setStep] = useState<'email' | 'sent'>('email')
  const [email, setEmail] = useState('')
  const [destination, setDestination] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmitEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const { destination: maskedDestination } = await forgotPassword(email)
      setDestination(maskedDestination)
      setStep('sent')
    } catch (err) {
      setError(describeForgotPasswordError(err, t))
    } finally {
      setSubmitting(false)
    }
  }

  const handleResend = async () => {
    setError(null)
    setSubmitting(true)
    try {
      const { destination: maskedDestination } = await forgotPassword(email)
      setDestination(maskedDestination)
    } catch (err) {
      setError(describeForgotPasswordError(err, t))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm rounded-lg border border-border bg-bg-elevated p-6 shadow-sm">
        {step === 'email' ? (
          <>
            <h1 className="mb-1 text-2xl font-semibold text-fg">{t('forgotPassword.title')}</h1>
            <p className="mb-6 text-sm text-fg-muted">{t('forgotPassword.subtitle')}</p>

            <form onSubmit={handleSubmitEmail} className="flex flex-col gap-4">
              <FormField
                id="email"
                label={t('forgotPassword.emailLabel')}
                type="email"
                autoComplete="email"
                placeholder={t('forgotPassword.emailPlaceholder')}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              {error && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/40 dark:text-red-200">
                  {error}
                </p>
              )}

              <Button type="submit" disabled={submitting || !email} className="w-full">
                {submitting ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
              </Button>
            </form>

            <p className="mt-6 border-t border-border pt-4 text-center text-sm">
              <Link className="text-fg-muted hover:text-fg" to="/login">
                {t('forgotPassword.backToSignIn')}
              </Link>
            </p>
          </>
        ) : (
          <>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/40">
              <svg
                aria-hidden="true"
                className="h-6 w-6 text-emerald-600 dark:text-emerald-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <title>{t('forgotPassword.emailIconTitle')}</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className="mb-1 text-2xl font-semibold text-fg">{t('forgotPassword.sentTitle')}</h1>
            <p className="mb-2 text-sm text-fg-muted">
              {t('forgotPassword.sentBodyKnownEmail', {
                email,
                destination: destination || email,
              })}
            </p>
            <p className="mb-6 text-sm text-fg-muted">{t('forgotPassword.sentBodyInstruction')}</p>

            {error && (
              <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/40 dark:text-red-200">
                {error}
              </p>
            )}

            <div className="flex flex-col gap-2">
              <Link
                to="/reset-password"
                search={{ email }}
                className="inline-flex w-full items-center justify-center rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:opacity-90 active:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fg"
              >
                {t('forgotPassword.continueToReset')}
              </Link>
              <Button
                type="button"
                variant="secondary"
                disabled={submitting}
                onClick={handleResend}
                className="w-full"
              >
                {submitting
                  ? t('forgotPassword.resendCodeSubmitting')
                  : t('forgotPassword.resendCode')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={submitting}
                onClick={() => {
                  setStep('email')
                  setError(null)
                }}
                className="w-full"
              >
                {t('forgotPassword.useDifferentEmail')}
              </Button>
            </div>

            <p className="mt-6 border-t border-border pt-4 text-center text-sm">
              <Link className="text-fg-muted hover:text-fg" to="/login">
                {t('forgotPassword.backToSignIn')}
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
